import { CUSTOMER_NOTES_MAX_LENGTH } from "@/domain/customer-notes";
import { create } from "zustand";
import { resolveDiscoveryPath } from "@/domain/discovery-navigation";
import {
  consumeEstimateRevisionIntent,
  permitsEstimateRevision,
  resumeEstimateRevision,
  type DiscoveryRevisionPermit,
} from "@/discovery/revision-intent";
import type { AppError } from "@/data/errors";
import {
  getDiscovery,
  saveBoundDiscovery,
  beginDiscoveryRevision,
  createLead,
  LeadNotificationError,
} from "@/data/firestore/discoveryRepository";
import {
  getLiveModel,
  getPublishedModel,
  initialDiscovery,
  startPublishedDiscovery,
} from "@/data/firestore/discoveryModelRepository";
import { discoveryPersistenceTarget, discoveryStorageKey } from "@/discovery/session-persistence";
import {
  loadModelSession,
  saveModelSession,
  MODEL_SESSION_PREFIX,
} from "@/discovery/model-session-persistence";
import {
  validateJourney,
  validateAnswers,
  validateDiscoveryModel,
  type PublishedDiscoveryModel,
} from "@/domain/discovery-model";
import { evaluateDiscovery } from "@/discovery/engine";
import { reconcileJourneyProgress, resumeJourney } from "@/discovery/journey-state";
import type {
  Discovery,
  DiscoveryAnswers,
  DiscoveryResult,
  DiscoverySessionContext,
  DiscoveryStage,
  Lead,
  LoadStatus,
} from "@/domain/types";
import { getCustomerAccess, watchCustomerAccess, type CustomerAccess } from "@/lib/access-control";
import { requireSystemAdmin } from "@/admin/access";

type Patch = {
  stage?: DiscoveryStage;
  questionIndex?: number;
  currentQuestionId?: string | null;
  answers?: DiscoveryAnswers;
  visitorNotes?: string;
  presentedQuestions?: NonNullable<Discovery["presentedQuestions"]>;
};
type State = {
  customerId: string | null;
  model: PublishedDiscoveryModel | null;
  context: DiscoverySessionContext | null;
  persistence: "local" | "firestore" | null;
  authenticatedUserId: string | null;
  discovery: Discovery | null;
  status: LoadStatus;
  error: AppError | null;
  notice: string | null;
  customerNotes: string;
  setCustomerNotes: (value: string) => void;
  prepare: (
    customerId: string,
    context: DiscoverySessionContext,
    inspection?: boolean,
    preview?: PublishedDiscoveryModel,
  ) => Promise<void>;
  begin: () => Promise<void>;
  improve: (permit: DiscoveryRevisionPermit) => Promise<void>;
  patch: (patch: Patch) => Promise<void>;
  complete: (result: DiscoveryResult, answers?: DiscoveryAnswers) => Promise<void>;
  submitContact: (contact: Lead["contact"], action: Lead["requestedAction"]) => Promise<void>;
  flush: () => Promise<void>;
  restart: () => Promise<void>;
  stop: () => void;
};
let generation = 0;
let stopSimulatorAccess: (() => void) | undefined;
let simulatorAccess: CustomerAccess | undefined;
let storageKey: string | null = null;
let inspectionMode = false;
let previewMode = false;
let pending = false;
let revisionPermit: DiscoveryRevisionPermit | undefined;
let queue: Promise<void> = Promise.resolve();
function sameAnswers(a: DiscoveryAnswers, b: DiscoveryAnswers) {
  const ids = Object.keys(a);
  return (
    ids.length === Object.keys(b).length &&
    ids.every((id) => JSON.stringify(a[id]) === JSON.stringify(b[id]))
  );
}
function offline(error: unknown) {
  return (
    ["unavailable", "deadline-exceeded"].includes(String((error as { code?: string })?.code)) ||
    (typeof navigator !== "undefined" && navigator.onLine === false)
  );
}
function save() {
  const s = useDiscoveryStore.getState();
  if (storageKey && s.customerId && s.discovery && s.model)
    saveModelSession(storageKey, {
      version: 2,
      customerId: s.customerId,
      discovery: s.discovery,
      model: s.model,
      pending,
      customerNotes: s.customerNotes,
      ...(revisionPermit ? { revisionId: revisionPermit.id } : {}),
    });
}
async function flushCurrent() {
  const s = useDiscoveryStore.getState();
  if (!pending || s.persistence !== "firestore" || !s.customerId || !s.discovery || !s.model)
    return;
  if (s.context?.mode === "simulator") {
    const access = simulatorAccess;
    if (useDiscoveryStore.getState().context !== s.context) return;
    if (!access || access.mode !== "manage" || access.userId !== s.authenticatedUserId) {
      useDiscoveryStore.setState({ persistence: "local", authenticatedUserId: null });
      pending = false;
      save();
      return;
    }
  }
  const permit = revisionPermit;
  const saved = await saveBoundDiscovery(s.customerId, s.discovery, s.model, permit);
  if (useDiscoveryStore.getState().discovery === s.discovery) {
    pending = false;
    if (permit === revisionPermit && !saved.revisionId) revisionPermit = undefined;
    useDiscoveryStore.setState({ discovery: saved });
    save();
  }
}
function schedule() {
  const token = generation;
  queue = queue.catch(() => undefined).then(flushCurrent);
  void queue.catch((error: unknown) => {
    if (token === generation && !offline(error))
      useDiscoveryStore.setState({ error: error as AppError });
  });
}
export const useDiscoveryStore = create<State>((set, get) => ({
  customerId: null,
  model: null,
  context: null,
  persistence: null,
  authenticatedUserId: null,
  discovery: null,
  status: "idle",
  error: null,
  notice: null,
  customerNotes: "",
  setCustomerNotes: (value) => {
    set({ customerNotes: value.slice(0, CUSTOMER_NOTES_MAX_LENGTH) });
    save();
  },
  prepare: async (customerId, requested, inspection = false, preview) => {
    if (
      get().customerId === customerId &&
      get().context?.mode === requested.mode &&
      get().context?.sourceUrl === requested.sourceUrl &&
      (!requested.sessionId || get().context?.sessionId === requested.sessionId) &&
      get().status === "ready" &&
      inspectionMode === inspection &&
      previewMode === Boolean(preview)
    )
      return;
    if (inspection) requireSystemAdmin();
    const token = ++generation;
    stopSimulatorAccess?.();
    stopSimulatorAccess = undefined;
    simulatorAccess = undefined;
    inspectionMode = inspection;
    previewMode = Boolean(preview);
    pending = false;
    revisionPermit = undefined;
    set({
      customerId,
      model: null,
      context: requested,
      discovery: null,
      persistence: null,
      authenticatedUserId: null,
      status: "loading",
      error: null,
      notice: null,
      customerNotes: "",
    });
    const access =
      requested.mode === "simulator" && !inspection && !preview
        ? await getCustomerAccess(customerId)
        : { mode: "read-only" as const, userId: null };
    if (token !== generation) return;
    simulatorAccess = access;
    const authenticatedUserId = access.mode === "manage" ? access.userId : null;
    const oldKey = discoveryStorageKey(customerId, requested);
    storageKey =
      inspection || preview ? null : oldKey.replace("qotta.discovery.v1", MODEL_SESSION_PREFIX);
    // Demo used to be browser-only. Start fresh instead of promoting an old local journey.
    if (storageKey && requested.mode === "demo") storageKey += ".persisted-demo.v1";
    if (storageKey && requested.mode === "simulator")
      storageKey += `.${encodeURIComponent(import.meta.env["VITE_FIREBASE_PROJECT_ID"] ?? "default")}.${encodeURIComponent(authenticatedUserId ?? "public")}`;
    try {
      const local = storageKey ? loadModelSession(storageKey, customerId) : null;
      let discovery = local?.discovery ?? null;
      let model = local?.model ?? null;
      let revisionId = local?.revisionId ?? discovery?.revisionId;
      const context = {
        ...requested,
        sessionId: discovery?.sessionId || requested.sessionId || crypto.randomUUID(),
      };
      const persistence =
        inspection || preview
          ? "local"
          : discoveryPersistenceTarget(requested.mode, authenticatedUserId);
      let pendingChanges = local?.pending ?? false;
      if (persistence === "firestore" && (discovery || requested.sessionId)) {
        try {
          const remote = await getDiscovery(customerId, discovery?.id ?? requested.sessionId);
          if (remote) {
            if (remote.context.mode !== requested.mode)
              throw new Error("Discovery mode cannot change. Start a fresh journey.");
            if (!remote.modelId)
              throw new Error(
                "Legacy journey cannot resume. Start over to begin a new model-backed journey.",
              );
            if (discovery && remote.modelId !== discovery.modelId)
              throw new Error("Saved journey model binding does not match the server.");
            const retryingNotification = discovery?.stage === "contact" && discovery.leadId &&
              discovery.leadId === remote.leadId;
            const pendingRevision =
              pendingChanges && revisionId && remote.revisionId === revisionId;
            if (
              (revisionId && remote.revisionId !== revisionId) ||
              (remote.result && !pendingRevision) ||
              !pendingChanges ||
              !discovery
            ) {
              discovery = retryingNotification ? { ...remote, stage: "contact" } : remote;
              pendingChanges = false;
              revisionId = remote.revisionId;
            }
            if (!model || model.id !== remote.modelId)
              model = await getPublishedModel(customerId, remote.modelId);
          } else if (discovery)
            throw new Error("The original journey is missing. Start over explicitly.");
        } catch (error) {
          if (!offline(error) || !discovery || !model) throw error;
        }
      }
      if (discovery && discovery.context.mode !== requested.mode)
        throw new Error("Discovery mode cannot change. Start a fresh journey.");
      if (discovery && model) {
        discovery = resumeJourney(model.model, discovery);
        validateJourney(model.model, discovery);
      }
      if (!discovery) {
        model = preview
          ? { ...preview, model: validateDiscoveryModel(preview.model) }
          : await getLiveModel(customerId);
        if (typeof window !== "undefined" && window.localStorage.getItem(oldKey))
          set({
            notice:
              "An older journey cannot safely resume. Start begins a fresh journey; its saved historical data is unchanged.",
          });
      }
      if (token !== generation) return;
      pending = pendingChanges;
      revisionPermit =
        discovery && revisionId
          ? resumeEstimateRevision(customerId, discovery, revisionId)
          : undefined;
      set({ model, context, persistence, authenticatedUserId, discovery, status: "ready",
        customerNotes: typeof local?.customerNotes === "string" ? local.customerNotes.slice(0, CUSTOMER_NOTES_MAX_LENGTH) : "",
      });
      save();
      if (persistence === "firestore" && requested.mode === "simulator") {
        stopSimulatorAccess = watchCustomerAccess(customerId, (next) => {
          if (token !== generation) return;
          if (next.pending && next.userId === authenticatedUserId) return;
          simulatorAccess = next;
          if (next.userId !== authenticatedUserId || (!next.pending && next.mode !== "manage")) {
            pending = false;
            revisionPermit = undefined;
            const state = get();
            const fresh = { ...context, sessionId: crypto.randomUUID() };
            // A new local simulator may use the public model, never another user's saved answers.
            storageKey = null;
            set({
              persistence: "local",
              authenticatedUserId: null,
              context: fresh,
              customerNotes: "",
              discovery: state.model ? initialDiscovery(state.model, fresh) : null,
            });
          }
        });
      }
      if (pending) schedule();
    } catch (error) {
      if (token === generation) set({ status: "error", error: error as AppError });
    }
  },
  begin: async () => {
    const s = get();
    if (!s.customerId || !s.context || !s.model || s.discovery) return;
    try {
      const localModel =
        s.persistence === "local" && !previewMode ? await getLiveModel(s.customerId) : s.model;
      const binding =
        s.persistence === "firestore"
          ? await startPublishedDiscovery(s.customerId, s.context, s.authenticatedUserId)
          : {
              model: localModel,
              discovery: initialDiscovery(localModel, s.context, s.authenticatedUserId),
            };
      if (get().context !== s.context) return;
      set(binding);
      pending = false;
      save();
    } catch (error) {
      set({ error: error as AppError });
      throw error;
    }
  },
  improve: async (permit) => {
    const s = get();
    if (
      !s.discovery?.result ||
      !s.model ||
      !s.customerId ||
      !permitsEstimateRevision(permit, s.customerId, s.discovery, true)
    )
      throw new Error("Improve intent is required to revise this Discovery.");
    await get().flush();
    if (get().discovery?.id !== s.discovery.id || get().context !== s.context) return;
    const current = get().discovery!;
    const last = resolveDiscoveryPath(s.model.model, current.answers).path.at(-1)!;
    const next =
      s.persistence === "firestore"
        ? await beginDiscoveryRevision(s.customerId, current, s.model, permit)
        : {
            ...current,
            revisionId: permit.id,
            stage: "questions" as const,
            currentQuestionId: last.question.id,
            currentStepId: last.stepId,
            questionIndex: last.questionIndex,
          };
    if (get().discovery?.id !== current.id || get().context !== s.context) return;
    consumeEstimateRevisionIntent(permit);
    revisionPermit = permit;
    set({ discovery: next });
    save();
  },
  patch: async (patch) => {
    const s = get();
    if (!s.discovery || !s.model) return;
    const revising = Boolean(
      s.customerId &&
      permitsEstimateRevision(revisionPermit, s.customerId, s.discovery) &&
      s.discovery.revisionId === revisionPermit?.id,
    );
    if ((s.discovery.result || s.discovery.revisionId) && !revising) {
      if (s.discovery.result && (patch.stage === "contact" || patch.stage === "result")) {
        set({ discovery: { ...s.discovery, stage: patch.stage } });
        save();
      }
      return;
    }
    let next = reconcileJourneyProgress(s.model.model, {
      ...s.discovery,
      ...patch,
      updatedAt: new Date(),
      firstAnswerAt:
        patch.answers && !s.discovery.firstAnswerAt ? new Date() : s.discovery.firstAnswerAt,
    });
    if (revising && patch.answers && !sameAnswers(next.answers, s.discovery.answers)) {
      next = { ...next, result: null, status: "in_progress", completedAt: null };
      let completeAnswers = true;
      try {
        validateAnswers(s.model.model, next.answers, true);
      } catch {
        completeAnswers = false;
      }
      if (completeAnswers)
        next = {
          ...next,
          result: evaluateDiscovery(s.model.model, next.answers),
          status: "completed",
          completedAt: new Date(),
        };
    }
    validateJourney(s.model.model, next);
    set({ discovery: next });
    pending = s.persistence === "firestore";
    save();
    if (pending) schedule();
  },
  complete: async (_result, answers) => {
    const s = get();
    if (!s.discovery || !s.model) return;
    const revising = Boolean(
      s.customerId &&
      permitsEstimateRevision(revisionPermit, s.customerId, s.discovery) &&
      s.discovery.revisionId === revisionPermit?.id,
    );
    if ((s.discovery.result || s.discovery.revisionId) && !revising) return;
    const progress = reconcileJourneyProgress(s.model.model, {
      ...s.discovery,
      answers: answers ?? s.discovery.answers,
    });
    const finalAnswers = progress.answers;
    validateAnswers(s.model.model, finalAnswers, true);
    const unchanged = s.discovery.result && sameAnswers(finalAnswers, s.discovery.answers);
    const result = unchanged ? s.discovery.result! : evaluateDiscovery(s.model.model, finalAnswers);
    const discovery: Discovery = {
      ...progress,
      answers: finalAnswers,
      result,
      status: "completed",
      stage: "result",
      completedAt: unchanged ? s.discovery.completedAt : new Date(),
      updatedAt: new Date(),
    };
    if (revising) delete discovery.revisionId;
    validateJourney(s.model.model, discovery);
    set({ discovery });
    if (s.persistence !== "firestore") revisionPermit = undefined;
    pending = s.persistence === "firestore";
    save();
    if (pending) schedule();
  },
  submitContact: async (contact, action) => {
    const s = get();
    if (!s.discovery?.result || !s.customerId || inspectionMode) return;
    if (previewMode || (s.context?.mode !== "live" && s.context?.mode !== "demo")) {
      set({ discovery: { ...s.discovery, stage: "sent", contactRequestedAt: new Date() } });
      save();
      return;
    }
    await get().flush();
    let id: string;
    try {
      id = await createLead(s.customerId, s.discovery.id, contact, action, (leadId) => {
        const current = get().discovery;
        if (current?.id === s.discovery!.id) {
          set({ discovery: { ...current, leadId, stage: "contact", contactRequestedAt: new Date() } });
          save();
        }
      }, s.customerNotes);
    } catch (error) {
      if (error instanceof LeadNotificationError && get().discovery?.id === s.discovery.id) {
        set({ discovery: { ...get().discovery!, leadId: error.leadId, stage: "contact", contactRequestedAt: new Date() } });
        save();
      }
      throw error;
    }
    const latest = get().discovery;
    if (latest?.id === s.discovery.id) {
      set({ discovery: { ...latest, leadId: id, stage: "sent", contactRequestedAt: new Date() } });
      save();
    }
  },
  flush: async () => {
    await queue.catch(() => undefined);
    await flushCurrent();
  },
  restart: async () => {
    const s = get();
    if (!s.customerId || !s.context) return;
    await get().flush();
    if (storageKey) window.localStorage.removeItem(storageKey);
    const context = { ...s.context, sessionId: "" };
    const preview = previewMode ? (s.model ?? undefined) : undefined;
    set({ status: "idle", discovery: null, context: null });
    await get().prepare(s.customerId, context, inspectionMode, preview);
  },
  stop: () => {
    generation++;
    stopSimulatorAccess?.();
    stopSimulatorAccess = undefined;
    simulatorAccess = undefined;
    storageKey = null;
    pending = false;
    revisionPermit = undefined;
    set({
      customerId: null,
      model: null,
      context: null,
      persistence: null,
      authenticatedUserId: null,
      discovery: null,
      status: "idle",
      error: null,
      notice: null,
      customerNotes: "",
    });
  },
}));
